import { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { NocDeviceMap } from '@/components/map/NocDeviceMap'
import { useAuthStore } from '@/stores/authStore'
import { adminService } from '@/services/admin.service'
import { useQuery } from '@tanstack/react-query'
import {
  RefreshCw,
  Rss,
  BatteryLow,
  Box,
  Activity,
  AlertTriangle,
  Search,
} from 'lucide-react'
import { formatDistanceToNow, parseISO } from 'date-fns'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface StatTileProps {
  title: string
  value: React.ReactNode
  subtitle: string
  gradient: string
  icon: React.ComponentType<{ className?: string }>
  href?: string
}

const StatTile = ({ title, value, subtitle, gradient, icon: Icon, href }: StatTileProps) => {
  const titleEl = href ? (
    <Link to={href} className="text-sm text-primary hover:underline">
      {title}
    </Link>
  ) : (
    <span className="text-sm text-muted-foreground">{title}</span>
  )

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-5">
        <div className="flex items-center">
          <div className="flex-1 min-w-0">
            <div className="mb-1">{titleEl}</div>
            <h4 className="text-2xl font-bold leading-tight">{value}</h4>
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          </div>
          <div
            className={`w-14 h-14 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${gradient}`}
          >
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const formatRelative = (iso?: string | null) => {
  if (!iso) return <span className="text-muted-foreground">N/A</span>
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true })
  } catch {
    return <span className="text-muted-foreground">N/A</span>
  }
}

const parseBattery = (battery?: string | null): number | null => {
  if (!battery) return null
  const n = parseInt(battery, 10)
  return Number.isNaN(n) ? null : n
}

const batteryBadgeClass = (pct: number | null): string => {
  if (pct === null) return 'text-muted-foreground'
  if (pct > 85) return 'text-green-600 font-semibold'
  if (pct > 75) return 'text-yellow-600 font-semibold'
  if (pct > 55) return 'text-orange-600 font-semibold'
  return 'text-red-600 font-semibold'
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AdminNocDashboardPage = () => {
  const { user } = useAuthStore()
  const [deviceFilter, setDeviceFilter] = useState('')

  const { data: noc, isLoading, error } = useQuery({
    queryKey: ['admin-noc-summary'],
    queryFn: () => adminService.getNocSummary(),
    staleTime: 60000,
    enabled: !!user?.isSuperUser,
  })

  const filteredDevices = useMemo(() => {
    if (!noc?.devices) return []
    const q = deviceFilter.trim().toLowerCase()
    if (!q) return noc.devices
    return noc.devices.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        d.serial_number?.toLowerCase().includes(q)
    )
  }, [noc?.devices, deviceFilter])

  if (!user?.isSuperUser) {
    return <Navigate to="/unauthorized" replace />
  }

  if (isLoading) return <LoadingSpinner />
  if (error || !noc) {
    return <ErrorMessage message="Failed to load NOC dashboard data. Please try again." />
  }

  const trendBadge =
    noc.sessions_trend_pct >= 0
      ? `(+${noc.sessions_trend_pct}%)`
      : `(${noc.sessions_trend_pct}%)`

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Network Operations Center</h1>
        <p className="text-muted-foreground">
          System-wide overview of devices, users, and activity
        </p>
      </div>

      {/* Row 1 — 6 stat cards in a 3-column grid (2 rows on desktop) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatTile
          title="Sessions"
          value={
            <>
              {noc.sessions_7d}{' '}
              <span className="text-sm text-green-600 font-medium">{trendBadge}</span>
            </>
          }
          subtitle="Logons last 7 days"
          gradient="bg-gradient-to-br from-purple-500 to-indigo-600"
          icon={RefreshCw}
          href="/users"
        />
        <StatTile
          title="Orbcomm Modems"
          value={`${noc.orbcomm_active} / ${noc.orbcomm_inactive}`}
          subtitle="Active / Inactive"
          gradient="bg-gradient-to-br from-violet-500 to-purple-700"
          icon={Rss}
        />
        <StatTile
          title="Low Battery"
          value={noc.low_battery_devices}
          subtitle="Battery level below 70%"
          gradient="bg-gradient-to-br from-orange-500 to-red-600"
          icon={BatteryLow}
        />
        <StatTile
          title="Devices"
          value={`${noc.devices_online_24h} / ${noc.devices_with_gps} / ${noc.deployed_devices} / ${noc.devices_total}`}
          subtitle="24 hours / GPS Fix / Modems / Total"
          gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
          icon={Box}
        />
        <StatTile
          title="Missed Transmissions"
          value={`${noc.missing_transmissions['24h']} / ${noc.missing_transmissions['3d']} / ${noc.missing_transmissions['7d']} / ${noc.missing_transmissions['30d']}`}
          subtitle="24h / 3d / 7d / 30d"
          gradient="bg-gradient-to-br from-blue-500 to-cyan-600"
          icon={Activity}
        />
        <StatTile
          title="Failed Sensors"
          value={noc.failed_sensors}
          subtitle="Devices reporting failure codes"
          gradient="bg-gradient-to-br from-green-500 to-lime-600"
          icon={AlertTriangle}
        />
      </div>

      {/* Row 2 — All Devices (2/3) + Recent Activity (1/3) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* All Devices table */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h6 className="font-semibold text-base">All Devices</h6>
              <div className="relative w-64 max-w-full">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or serial..."
                  value={deviceFilter}
                  onChange={(e) => setDeviceFilter(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>
            </div>
            <div className="rounded-md border max-h-[500px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>Serial</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead className="text-right">Battery</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDevices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                        No devices found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDevices.map((d) => {
                      const pct = parseBattery(d.battery_level)
                      return (
                        <TableRow key={d.id}>
                          <TableCell>
                            <Link
                              to={`/devices/${d.id}`}
                              className="text-primary hover:underline font-mono text-sm"
                            >
                              {d.serial_number}
                            </Link>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{d.name}</TableCell>
                          <TableCell className="text-sm whitespace-nowrap">
                            {formatRelative(d.last_seen)}
                          </TableCell>
                          <TableCell className={`text-right ${batteryBadgeClass(pct)}`}>
                            {pct !== null ? `${pct}%` : 'N/A'}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity table */}
        <Card>
          <CardContent className="p-5">
            <h6 className="font-semibold text-base mb-3">Recent Activity</h6>
            <div className="rounded-md border max-h-[500px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background">
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Last Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {noc.recent_activity.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="text-center text-muted-foreground py-6">
                        No recent activity
                      </TableCell>
                    </TableRow>
                  ) : (
                    noc.recent_activity.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <Link
                            to={`/users/${u.id}`}
                            className="text-primary hover:underline text-sm"
                          >
                            {u.first_name || ''} {u.last_name || ''}
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap">
                          {formatRelative(u.last_action)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3 — Full-width device map with battery legend */}
      <Card>
        <CardContent className="p-5">
          <NocDeviceMap devices={noc.devices} height="600px" />
        </CardContent>
      </Card>
    </div>
  )
}
